import {normalizeName} from "@/helpers/import/matchNames";
import {OPTION_LIST, PRODUCT, USE_PREFIX} from "@/helpers/import/buildImportReferences";

const LOCALES = ['fr', 'nl', 'en'];
const CHANNELS = [
    {source: 'APP', suffix: 'App'},
    {source: 'WEB', suffix: 'Web'},
    {source: 'KIOSK', suffix: 'Kiosk'},
];

function validRows(sheets, name) {
    return (sheets[name]?.rows || []).filter(row => !row.excluded);
}

function localized(values, prefix) {
    return Object.fromEntries(LOCALES
        .map(locale => [locale, values[`${prefix}${locale.charAt(0).toUpperCase()}${locale.slice(1)}`]])
        .filter(([, value]) => value));
}

function isSet(value) {
    return value !== null && value !== undefined;
}

function itemPriceOverride(values) {
    if (values.free) return null;
    const channelValue = (channel, side) => values[`price${channel.suffix}${side}`];
    const hasPrice = isSet(values.priceIn) || isSet(values.priceOut)
        || CHANNELS.some(channel => isSet(channelValue(channel, 'In')) || isSet(channelValue(channel, 'Out')));
    if (!hasPrice) return null;
    return Object.fromEntries(CHANNELS.map(channel => {
        const priceIn = channelValue(channel, 'In') ?? values.priceIn ?? values.priceOut ?? null;
        const priceOut = channelValue(channel, 'Out') ?? values.priceOut ?? priceIn;
        return [channel.source, {priceVatInclIn: priceIn, priceVatInclOut: priceOut}];
    }));
}

function createItemProductResolver(sheets, choices) {
    const rows = validRows(sheets, 'Products');
    const byName = new Map();
    rows.forEach(row => {
        const key = normalizeName(row.values.name);
        byName.set(key, [...(byName.get(key) || []), row]);
    });
    const byRowNumber = new Map(rows.map(row => [row.rowNumber, row]));
    return ({product, productGroup}) => {
        const sameName = byName.get(normalizeName(product)) || [];
        const matches = productGroup
            ? sameName.filter(row => normalizeName(row.values.group) === normalizeName(productGroup))
            : sameName;
        if (matches.length === 1) return matches[0];
        const choice = choices.get(`${PRODUCT}:${normalizeName(product)}|${normalizeName(productGroup)}`);
        return choice?.startsWith(USE_PREFIX) ? byRowNumber.get(Number(choice.slice(USE_PREFIX.length))) || null : null;
    };
}

export function buildImportOptionLists(sheets, choices) {
    const lists = [];
    const byName = new Map();
    const addList = (name, values = {}) => {
        const list = {
            key: normalizeName(name),
            name,
            nameLocalized: localized(values, 'name'),
            description: localized(values, 'description'),
            fromFile: !!values.name,
            settings: {
                minChoice: values.minChoice ?? null,
                maxChoice: values.maxChoice ?? null,
                forceChoice: values.forceChoice ?? null,
            },
            items: [],
            productRows: new Set(),
        };
        lists.push(list);
        byName.set(list.key, list);
        return list;
    };
    validRows(sheets, 'OptionLists').forEach(row => {
        if (!byName.has(normalizeName(row.values.name))) addList(row.values.name, row.values);
    });

    const resolveList = name => {
        if (!name) return null;
        const found = byName.get(normalizeName(name));
        if (found) return found;
        const choice = choices.get(`${OPTION_LIST}:${normalizeName(name)}`);
        if (choice?.startsWith(USE_PREFIX)) return byName.get(normalizeName(choice.slice(USE_PREFIX.length))) || null;
        return addList(name);
    };

    validRows(sheets, 'Products').forEach(row => {
        (row.values.optionLists || []).forEach(name => resolveList(name)?.productRows.add(row.rowNumber));
    });

    const resolveProduct = createItemProductResolver(sheets, choices);
    validRows(sheets, 'OptionListItems').forEach(row => {
        const list = resolveList(row.values.list);
        const product = resolveProduct(row.values);
        if (!list || !product) return;
        list.items.push({
            rowNumber: row.rowNumber,
            productRow: product.rowNumber,
            productName: product.values.name,
            productGroup: product.values.group,
            free: !!row.values.free,
            without: !!row.values.without,
            allowMultipleChoice: !!row.values.allowMultipleChoice,
            maxChoice: row.values.maxChoice ?? null,
            includeOptionLists: !!row.values.includeOptionLists,
            priceOverride: itemPriceOverride(row.values),
        });
    });

    return {lists, resolveList};
}
